import type * as THREE from 'three';
import { XR } from '../config';

type ErrorReporter = (message: string) => void;

/**
 * VR 입장 버튼 배선 (하이브리드).
 * three의 VRButton과 동일한 견고한 세션 수명주기(지원체크 → requestSession →
 * setSession → end 토글)를 따르되, DOM 버튼은 프로토타입의 커스텀 한글/골드
 * 버튼(#vr)을 그대로 구동한다.
 */
export function setupVRButton(
  renderer: THREE.WebGLRenderer,
  button: HTMLButtonElement,
  reportError: ErrorReporter,
): void {
  let currentSession: XRSession | null = null;

  async function onSessionStarted(session: XRSession): Promise<void> {
    session.addEventListener('end', onSessionEnded);
    await renderer.xr.setSession(session);
    button.textContent = XR.labels.exit;
    currentSession = session;
  }

  function onSessionEnded(): void {
    currentSession?.removeEventListener('end', onSessionEnded);
    button.textContent = XR.labels.enter;
    currentSession = null;
  }

  function requestSession(): void {
    void navigator.xr!
      .requestSession('immersive-vr', { optionalFeatures: [...XR.optionalFeatures] })
      .then(onSessionStarted)
      .catch((e: unknown) => {
        const message = e instanceof Error ? e.message : String(e);
        reportError(`${XR.labels.enterFailed}: ${message}`);
      });
  }

  function enable(): void {
    button.disabled = false;
    button.textContent = XR.labels.enter;
    button.addEventListener('click', () => {
      if (currentSession === null) requestSession();
      else void currentSession.end();
    });
  }

  function disable(label: string): void {
    button.disabled = true;
    button.textContent = label;
  }

  if (!navigator.xr) {
    disable(window.isSecureContext ? XR.labels.noWebXR : XR.labels.needsHttps);
    return;
  }

  void navigator.xr
    .isSessionSupported('immersive-vr')
    .then((supported) => (supported ? enable() : disable(XR.labels.unsupported)))
    .catch(() => disable(XR.labels.checkFailed));
}
